import React, { useCallback, useState } from 'react'
import { useLotteryData } from '../../hooks'
import { drawLottery } from '../../../api/merchantService'
import confetti from 'canvas-confetti'
import { Toast } from 'antd-mobile'
import NoChancesView from './NoChancesView'
import ScratchCardView from './ScratchCardView'
import ResultModal from './ResultModal'

const ScratchArea: React.FC = () => {
  const [isScratching, setIsScratching] = useState(false)
  const [showResult, setShowResult] = useState(false)
  const [prizeName, setPrizeName] = useState('')
  const [hasMoreChances, setHasMoreChances] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [scratchCardKey, setScratchCardKey] = useState(Date.now())
  const { lotteryChances, currentMerchant, currentPoints, pointsPerLotteryNum } = useLotteryData()

  // 增强的confetti效果
  const celebrateWithConfetti = async () => {
    const duration = 3000
    const animationEnd = Date.now() + duration
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1000 }

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min
    }

    // 第一波爆炸效果
    await confetti({
      ...defaults,
      particleCount: 100,
      origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      colors: ['#FF5A5A', '#FF8E8E', '#FFB6B6', '#FFFFFF', '#FFD700'],
    })

    // 第二波爆炸效果
    setTimeout(() => {
      confetti({
        ...defaults,
        particleCount: 50,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#FF5A5A', '#FF8E8E', '#FFB6B6', '#FFFFFF', '#FFD700'],
      })
    }, 250)

    // 持续的小爆炸效果
    const interval = setInterval(function () {
      const timeLeft = animationEnd - Date.now()

      if (timeLeft <= 0) {
        return clearInterval(interval)
      }

      const particleCount = 50 * (timeLeft / duration)

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.9), y: Math.random() - 0.2 },
        colors: ['#FF5A5A', '#FF8E8E', '#FFB6B6', '#FFFFFF', '#FFD700'],
      })
    }, 250)

    // 金色星星效果
    setTimeout(() => {
      confetti({
        particleCount: 30,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.8 },
        colors: ['#FFD700', '#FFA500', '#FF6347'],
      })
      confetti({
        particleCount: 30,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.8 },
        colors: ['#FFD700', '#FFA500', '#FF6347'],
      })
    }, 500)
  }

  const handleComplete = useCallback(async () => {
    if (isScratching) return

    setIsScratching(true)
    setErrorMessage('')

    try {
      const result = await drawLottery()
      setPrizeName(result.prizeName)
      setHasMoreChances(result.hasNextDrawChance)
      setShowResult(true)

      // 触发增强的庆祝动画
      await celebrateWithConfetti()
    } catch (error: any) {
      console.error('Failed to draw lottery:', error)
      setErrorMessage(error?.message || '抽奖失败，请重试')

      Toast.show({
        content: error?.message || '抽奖失败，请重试',
        position: 'center',
        duration: 2000,
      })
    } finally {
      setIsScratching(false)
    }
  }, [isScratching])

  const handleCloseResult = useCallback(async () => {
    setShowResult(false)
    setIsRefreshing(true)

    try {
      // 简单直接的方案：刷新页面确保所有数据同步
      // 这避免了组件间状态同步的复杂性
      Toast.show({
        content: '🔄 正在更新数据...',
        position: 'center',
        duration: 1000,
      })

      // 等待一秒后刷新页面，给用户时间看到提示
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } catch (error) {
      console.error('Failed to refresh data:', error)
      Toast.show({
        content: '⚠️ 数据更新失败，建议刷新页面',
        position: 'center',
        duration: 2000,
      })
      setIsRefreshing(false)
    }
  }, [])

  const handleRetry = useCallback(() => {
    setErrorMessage('')
    // 重置刮卡组件
    setScratchCardKey(Date.now())
    handleComplete()
  }, [handleComplete])

  // 当抽奖次数变化时，重置刮卡组件
  React.useEffect(() => {
    setScratchCardKey(Date.now())
  }, [lotteryChances])

  if (!currentMerchant || lotteryChances <= 0) {
    const pointsNeeded = pointsPerLotteryNum - (parseInt(String(currentPoints || 0), 10) || 0)
    return <NoChancesView pointsNeeded={pointsNeeded} />
  }

  return (
    <div style={{ position: 'relative' }}>
      <ScratchCardView key={scratchCardKey} onComplete={handleComplete} isLoading={isScratching || isRefreshing} />

      {/* 简化的加载状态 */}
      {(isScratching || isRefreshing) && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(255, 255, 255, 0.95)',
            padding: '16px 24px',
            borderRadius: 12,
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            zIndex: 5,
          }}
        >
          <div style={{ color: '#FF5A5A', fontSize: 16, fontWeight: 600 }}>
            {isRefreshing ? '🔄 更新数据中...' : '🎯 正在抽奖...'}
          </div>
        </div>
      )}

      {/* 错误重试按钮 */}
      {errorMessage && !isScratching && !isRefreshing && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(255, 255, 255, 0.95)',
            padding: '20px',
            borderRadius: 12,
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            zIndex: 10,
          }}
        >
          <div style={{ color: '#FF5A5A', marginBottom: 12, fontSize: 16 }}>{errorMessage}</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button
              onClick={handleRetry}
              style={{
                background: '#FF5A5A',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 14,
                whiteSpace: 'nowrap',
                minWidth: 'auto',
              }}
            >
              🔄 重试
            </button>
          </div>
        </div>
      )}

      <ResultModal
        opened={showResult}
        onClose={handleCloseResult}
        prizeName={prizeName}
        hasMoreChances={hasMoreChances}
      />
    </div>
  )
}

export default ScratchArea
